import React, { ReactNode, Suspense, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import CssBaseline from '@material-ui/core/CssBaseline';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import { WithTranslation, withTranslation } from 'react-i18next';
import { WithStyles, withStyles } from '@material-ui/core/styles';
import PersonIcon from '@material-ui/icons/Person';
import SearchIcon from '@material-ui/icons/Search';
import { Link as RouterLink } from 'react-router-dom';
import { History } from 'history';
// Components
import Breadcrumb from '../breadcrumb';
import CommandPalette, { getModifierLabel, useCommandPaletteShortcut } from '../command-palette';
import { Dialog } from '../dialog';
import MenuDrawer from '../menu-drawer';
import { Logo } from '../menu-drawer/menu-drawer';
import { TooltipButton } from '../tooltip-button';
import MainLoading from '../main-loading';
import LoadingError from '../loading-error';
// Modules
import { actions as homeActions, selectors as homeSelectors, UserStorage } from '../../redux/reducers/home';
import { ErrorPayload } from '../../redux/reducers/errors';
import { selectors as riderSelectors } from '../../redux/reducers/riders';
import { RiderReserver } from '../../redux/reducers/api-types';
// Ressources
import * as Images from '../../assets/images';
import withGlobalStyles from '../../utils/global-styles';
import { getHistory } from '../../utils/historyUtils';
import ObjectUtils from '../../utils/objectUtils';
// import StringUtils from '../../utils/stringUtils';
import RecentUtils from '../../utils/recentUtils';
import UserDataUtils from '../../utils/userDataUtils';
import UserUtils, { LOCAL_STORAGE_EVENT, RightDef } from '../../utils/userUtils';
import styles from './app-style';
import { paths } from '../../routes/paths';
import StringUtils from '../../utils/stringUtils';
import { clsx } from '../../utils/htmlUtils';
import ActionsDropdown from '../ActionsDropdown';
import { Button } from '@material-ui/core';
import { ActionFunction, BubbleInfo, HeaderProps } from '../../local-types';

// Easter Eggs
const Iconfall = React.lazy(() => import('../../easterEggs/iconfall'));

function getEasterDate(year) {
    const f = Math.floor;
    const a = year % 19;
    const b = f(year / 100);
    const c = year % 100;
    const d = f(b / 4);
    const e = b % 4;
    const f1 = f((b + 8) / 25);
    const g = f((b - f1 + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = f(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = f((a + 11 * h + 22 * l) / 451);
    const month = f((h + l - 7 * m + 114) / 31); // 3 = March, 4 = April
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(year, month - 1, day);
}

/**
 * A component where we render the skeleton of the website.
 * Here should be placed headers, footers, menus, etc...
 * In short, anything that has to appear in every page of the application
 */

interface AppProps extends WithTranslation, WithStyles {
    globalClasses: any;
    noFav?: boolean;
    saveFavSearch?: boolean;
    needRights?: string | RightDef | RightDef[];
    hasRole?: boolean;
    loading?: boolean;
    /** Error of the request `loading` is waiting on: swaps the spinner for a retry panel. */
    loadingError?: ErrorPayload;
    onRetry?: () => void;
    disableBreadcrumb?: boolean;
    header?: HeaderProps;
    contentStyle?: React.CSSProperties;
    foundRider: RiderReserver;
    userStorage: UserStorage;
    menusLoaded: boolean;
    loadMenus: () => any;
    toggleFavorite: (path: string, name?: string, removeIfExist?: boolean) => any;
}

const ERROR_403 = [
    'https://i.pinimg.com/originals/e4/e2/19/e4e2193e94554e6cc2e64b7f2a778723.gif',
    'https://www.clipartmax.com/png/middle/242-2421657_you-shall-not-pass-don-t-text-and-drive-sign.png',
];

const App: React.FC<AppProps> = (props): JSX.Element => {
    const {
        t,
        classes,
        globalClasses,
        header,
        noFav,
        saveFavSearch,
        needRights,
        hasRole = true,
        loading = false,
        loadingError,
        onRetry,
        disableBreadcrumb,
        children,
        foundRider,
        userStorage,
        menusLoaded,
        contentStyle,
    } = props;

    const {
        title,
        subtitle,
        anchors,
        actions,
        additionalActions,
        button,
        bubble = {} as BubbleInfo,
    }: HeaderProps = header || {};
    const { text = '', visible = false, tooltip = '', colorEnable = false }: BubbleInfo = bubble;

    const backToReserver = [
        paths.reserver,
        `${paths.reserver}/\\d*/add`,
        `${paths.bookings}/\\d*`,
        `${paths.reservations}/\\d*`,
        `${paths.reservations}/\\d*/edit`,
    ];

    //===========================================//
    //=============== Easter Eggs ===============//
    //===========================================//
    const user = UserUtils.getUser();

    const today = new Date();
    const easter = getEasterDate(today.getFullYear());
    const isChristmasEve = today.getDate() === 24 && today.getMonth() === 11; // 24th December
    const isGaletteDesRois = today.getDate() === 6 && today.getMonth() === 0; // 6th January
    const isAutumnEve = today.getDate() === 22 && today.getMonth() === 8; // 22nd September
    const isBackToSchool = today.getDate() === 1 && today.getMonth() === 8; // 1st September
    const isEaster = today.getDate() === easter.getDate() && today.getMonth() === easter.getMonth();
    const isHalloween = today.getDate() === 31 && today.getMonth() === 9; // 31st October
    const isValentinesDay = today.getDate() === 14 && today.getMonth() === 1; // 14th February
    const isBastilleDay = today.getDate() === 14 && today.getMonth() === 6; // 14th July
    const isMusicDay = today.getDate() === 21 && today.getMonth() === 5; // 21th June
    const isFriday13th = today.getDate() === 13 && today.getDay() === 5; // All Friday 13th
    const isNewYear = today.getDate() === 1 && today.getMonth() === 0; // 1st January
    const isChandeleur = today.getDate() === 2 && today.getMonth() === 1; // 2nd February
    const isSpringEve = today.getDate() === 20 && today.getMonth() === 2; // 20th March
    const isAprilFool = today.getDate() === 1 && today.getMonth() === 3; // 1st April
    const isLabourDay = today.getDate() === 1 && today.getMonth() === 4; // 1st May
    const isFeteDesLumieres = today.getDate() === 8 && today.getMonth() === 11; // 8th December
    const isBeaujolais =
        today.getMonth() === 10 && today.getDay() === 4 && today.getDate() >= 15 && today.getDate() <= 21; // 3rd Thursday of November
    const mardiGras = new Date(easter);
    mardiGras.setDate(easter.getDate() - 47); // 47 days before Easter
    const isMardiGras = today.getDate() === mardiGras.getDate() && today.getMonth() === mardiGras.getMonth();
    const isSaintPatrick = today.getDate() === 17 && today.getMonth() === 2; // 17th March
    const isStarWarsDay = today.getDate() === 4 && today.getMonth() === 4; // 4th May
    const isCatDay = today.getDate() === 8 && today.getMonth() === 7; // 8th August
    const isWinterEve = today.getDate() === 21 && today.getMonth() === 11; // 21st December
    const isSaintFannyDay =
        user?.firstname?.toLowerCase() === 'fanny' &&
        user?.lastname?.toLowerCase() === 'gomer' &&
        today.getDate() === 9 &&
        today.getMonth() === 2; // Fanny - 9 March
    const isSaintKimDay =
        user?.firstname?.toLowerCase() === 'kim' &&
        user?.lastname?.toLowerCase() === 'chaing' &&
        today.getDate() === 29 &&
        today.getMonth() === 5; // Kim - 29 Juin

    const events = [
        isChristmasEve,
        isGaletteDesRois,
        isAutumnEve,
        isBackToSchool,
        isEaster,
        isHalloween,
        isValentinesDay,
        isBastilleDay,
        isMusicDay,
        isFriday13th,
        isNewYear,
        isChandeleur,
        isSpringEve,
        isAprilFool,
        isLabourDay,
        isFeteDesLumieres,
        isBeaujolais,
        isMardiGras,
        isSaintPatrick,
        isStarWarsDay,
        isCatDay,
        isWinterEve,
        isSaintFannyDay,
        isSaintKimDay,
    ];

    //=======================================//
    //=============== State =================//
    //=======================================//

    const [random403] = useState<number>(Math.floor(Math.random() * ERROR_403.length));
    const [paletteOpen, setPaletteOpen] = useState<boolean>(false);

    const openPalette = React.useCallback(() => setPaletteOpen(true), []);
    const closePalette = React.useCallback(() => setPaletteOpen(false), []);
    useCommandPaletteShortcut(openPalette);
    const paletteShortcut = `${getModifierLabel()} K`;

    const history: History<any> = getHistory();
    const path: string = `${window.location.pathname}${saveFavSearch ? window.location.search : ''}`;
    const favIconView: ReactNode = Logo(Images.HeartIcon, { marginRight: 0 });
    const favIconFillView: ReactNode = Logo(Images.HeartFillIcon, {
        marginRight: 0,
    });
    const isFav: boolean = UserDataUtils.isFavorites(userStorage, path);

    // The favorites are needed on every page (the header fav button), not only on the home page:
    // without them loaded, adding a favorite would overwrite the whole stored list.
    useEffect(() => {
        if (!noFav && !menusLoaded) {
            props.loadMenus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [noFav, menusLoaded]);

    // Feed the command palette history. Titles are filled once the record is loaded, hence
    // the dependency on `title`: the entry is rewritten in place rather than duplicated.
    const recentPath: string = `${window.location.pathname}${window.location.search}`;
    useEffect(() => {
        if (UserUtils.checkRights(needRights) && hasRole) {
            RecentUtils.pushRecentPage({ to: recentPath, title, subtitle });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recentPath, title, subtitle]);

    //=======================================//
    //=============== Actions ===============//
    //=======================================//

    const [isRaining, setIsRaining] = useState<boolean>(
        UserUtils.getPref(LOCAL_STORAGE_EVENT) !== null ? UserUtils.getPref(LOCAL_STORAGE_EVENT) : true,
    );

    const handleDisabledEvent = (): void => {
        UserUtils.setPref(LOCAL_STORAGE_EVENT, !isRaining);
        setIsRaining((prev) => !prev);
    };

    const handleFav = (fields?): void => {
        props.toggleFavorite(path, fields?.name);
    };

    const goBack = (e: { preventDefault: () => void }): void => {
        e.preventDefault();
        history.goBack();
    };

    //======================================//
    //=============== Render ===============//
    //======================================//

    const renderCustomButton = (): JSX.Element => (
        <TooltipButton title={t('fav_add')} icon={favIconView} style={{ marginRight: 0 }} />
    );

    const renderTextField = (): JSX.Element => (
        <TextField
            autoFocus
            margin="dense"
            id="name"
            label={t('fav_add_label')}
            defaultValue={title}
            name="name"
            fullWidth
        />
    );

    const renderFavView = (): JSX.Element => {
        // Hidden until `menus` is loaded: clicking it before would mean writing a list
        // built from an empty state, wiping the favorites already stored server side.
        if (!noFav && menusLoaded) {
            if (!isFav) {
                return (
                    <Dialog
                        btnTitle={t('fav_add')}
                        description={t('fav_add_description')}
                        customButton={renderCustomButton()}
                        formComponent={renderTextField()}
                        onOK={handleFav}
                    />
                );
            } else {
                return (
                    <span onClick={handleFav}>
                        <TooltipButton title={t('fav_remove')} icon={favIconFillView} style={{ marginRight: 0 }} />
                    </span>
                );
            }
        }

        return null;
    };

    const renderActionMenu = (): JSX.Element => {
        if (actions?.length > 0) {
            return <ActionsDropdown menuItems={actions} />;
        }
        return null;
    };

    const renderReserverHome = (): JSX.Element => {
        let found = false;
        for (let i = 0; i < backToReserver.length; i++) {
            if (path.match(new RegExp('^' + backToReserver[i] + '$', 'g'))) {
                found = true;
                break;
            }
        }

        if (found && foundRider?.id) {
            return (
                <TooltipButton
                    title={t('goto_reserver', {
                        fullname: StringUtils.userFullname(foundRider),
                    })}
                >
                    <IconButton
                        aria-label="close"
                        color={'inherit'}
                        size="medium"
                        component={RouterLink}
                        to={`${paths.reserver}/${foundRider.id}`}
                    >
                        <PersonIcon fontSize="inherit" />
                    </IconButton>
                </TooltipButton>
            );
        }
        return null;
    };

    const renderAdditionnalActions = (actions: ActionFunction[]): JSX.Element[] => {
        return (
            actions &&
            actions.map((item) => (
                <TooltipButton title={item.label} key={item.label}>
                    <Button color="secondary" variant="contained" onClick={item.func}>
                        {item.label}
                    </Button>
                </TooltipButton>
            ))
        );
    };

    // const renderIconView = (): React.ReactNode => {
    //     if (icon && icon === 'default') {
    //         return Logo(getItemWithColor(t)?.customIcon, {}, 50);
    //     } else if (icon && icon !== 'default') {
    //         return icon;
    //     }
    //     return null;
    // };

    // const renderAnchors = (): JSX.Element[] => {
    //     if (anchors?.length) {
    //         return anchors.map((anchor) => (
    //             <TooltipButton key={anchor.label} title={t('goto', { txt: StringUtils.upperFirst(anchor.label) })}>
    //                 <a className={clsx(classes.anchor, 'QuickLinks')} href={anchor.href}>
    //                     {StringUtils.upperFirst(anchor.label)}
    //                 </a>
    //             </TooltipButton>
    //         ));
    //     }
    //     return null;
    // };

    const hasRights = UserUtils.checkRights(needRights) && hasRole;
    const showDrawer = title || subtitle || !noFav || !disableBreadcrumb || actions || anchors || button;
    const isEvent = events.some((e) => e);

    return (
        <div className={classes.app}>
            <CssBaseline />
            <CommandPalette open={paletteOpen} onClose={closePalette} />
            {isEvent && isRaining && (
                <Suspense fallback={null}>
                    {isChristmasEve && <Iconfall icons={['❄', '⛄', '🎄', '🎁']} />}
                    {isGaletteDesRois && <Iconfall icons={['👑', '🥧', '⚜️']} />}
                    {isAutumnEve && <Iconfall icons={['🍂']} />}
                    {isBackToSchool && <Iconfall icons={['🎒', '✏️', '📚', '📐', '🖍️', '🍎']} />}
                    {isEaster && <Iconfall icons={['🐇', '🥚']} />}
                    {isValentinesDay && <Iconfall icons={['❤️', '🌹', '🍫', '💌', '🎁', '💘']} />}
                    {isBastilleDay && <Iconfall icons={['🇫🇷', '🎆', '🥖', '🎇']} />}
                    {isSaintFannyDay && <Iconfall icons={['🌸', '🎄', '🥳', '🎉']} />}
                    {isSaintKimDay && (
                        <Iconfall
                            icons={['🌸', '🎏', '🎐', '🏯', '🚅', '🗻', '🍘', '⛩️', '🍙', '🍣', '🍥', '🍡', '🎌', '🗼']}
                        />
                    )}
                    {isFriday13th && <Iconfall icons={['🍀', '🧲', '🤞', '🧧']} />}
                    {isHalloween && <Iconfall icons={['👻', '🎃', '🕸️', '🦇', '🦴']} />}
                    {isMusicDay && <Iconfall icons={['🎶', '🎸', '🎷', '🎻']} />}
                    {isNewYear && <Iconfall icons={['🥂', '🍾', '🎆', '🎊']} />}
                    {isChandeleur && <Iconfall icons={['🥞', '🍯', '🍫', '🕯️']} />}
                    {isSpringEve && <Iconfall icons={['🌷', '🌸', '🐝', '🌼']} />}
                    {isAprilFool && <Iconfall icons={['🐟', '🐠', '🐡', '🎣']} />}
                    {isLabourDay && <Iconfall icons={['🌱', '🔔', '🤍']} />}
                    {isFeteDesLumieres && <Iconfall icons={['🕯️', '✨', '💡']} />}
                    {isBeaujolais && <Iconfall icons={['🍷', '🍇', '🧀', '🥖']} />}
                    {isMardiGras && <Iconfall icons={['🎭', '🍩', '🎉', '🪅']} />}
                    {isSaintPatrick && <Iconfall icons={['☘️', '🍀', '🍺', '🎩']} />}
                    {isStarWarsDay && <Iconfall icons={['⚔️', '🚀', '🌌', '🤖']} />}
                    {isCatDay && <Iconfall icons={['🐱', '🐈', '🧶', '🐾']} />}
                    {isWinterEve && <Iconfall icons={['❄️', '🌙', '⭐']} />}
                </Suspense>
            )}
            <MenuDrawer isEvent={{ isActive: isEvent, raining: isRaining, onChange: handleDisabledEvent }}>
                {showDrawer && (
                    <div
                        id="header"
                        className={clsx(
                            globalClasses.header,
                            globalClasses.topBarContentSpacing,
                            anchors || actions ? globalClasses.stickyHeader : globalClasses.notStickyHeader,
                        )}
                    >
                        <div className={globalClasses.centerVertical}>
                            {!disableBreadcrumb && (
                                <>
                                    <TooltipButton
                                        title={t('common:back')}
                                        icon={Logo(Images.BackIcon, { marginRight: 0 }, 8)}
                                        onClick={goBack}
                                        style={{ marginRight: 12 }}
                                    />
                                    <Breadcrumb />
                                </>
                            )}
                            <span style={{ flex: 1 }} />
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                {renderAdditionnalActions(additionalActions)}
                                {renderReserverHome()}
                                <TooltipButton
                                    title={t('command_palette:open', { shortcut: paletteShortcut })}
                                    icon={<SearchIcon fontSize="small" />}
                                    onClick={openPalette}
                                    style={{ marginRight: 0 }}
                                />
                                {renderFavView()}
                                {renderActionMenu()}
                            </div>
                        </div>
                        {disableBreadcrumb && noFav && <div style={{ height: 16 }} />}
                        <div style={{ display: 'flex' }}>
                            <div className={globalClasses.centerVertical}>
                                {title && (
                                    <Typography variant="h4" component="h1" className={classes.title} color="secondary">
                                        {title}
                                    </Typography>
                                )}
                                {visible && text && (
                                    <TooltipButton title={tooltip || text}>
                                        <span
                                            className={clsx(
                                                classes.bubble,
                                                colorEnable ? classes.enabledColor : classes.disabledColor,
                                            )}
                                        >
                                            {text}
                                        </span>
                                    </TooltipButton>
                                )}
                                <span style={{ flex: 1 }} />
                                {button}
                            </div>
                            {/* {subtitle && (
                                        <Typography variant="subtitle1" color="textSecondary" component="p">
                                            {subtitle}
                                        </Typography>
                                    )} */}
                            {/* <div className={classes.anchors}>{renderAnchors()}</div> */}
                        </div>
                    </div>
                )}

                <div
                    className={globalClasses.contentSpacing}
                    style={contentStyle && !ObjectUtils.isEmpty(contentStyle) ? contentStyle : {}}
                >
                    {hasRights ? (
                        loading && loadingError ? (
                            <LoadingError error={loadingError} onRetry={onRetry} />
                        ) : loading ? (
                            <MainLoading center />
                        ) : (
                            children
                        )
                    ) : (
                        <>
                            {t('common:rights.noView')}
                            <img
                                src={ERROR_403[random403]}
                                alt={'Error 403'}
                                style={{ width: '-webkit-fill-available' }}
                                referrerPolicy="no-referrer"
                            />
                        </>
                    )}
                </div>
            </MenuDrawer>
        </div>
    );
};

const AppWithTranslation = withTranslation(['app', 'common'])(App);

const AppWithStyleAndTranslation = withGlobalStyles(withStyles(styles)(AppWithTranslation));

const mapStateToProps = (state) => ({
    foundRider: riderSelectors.foundRider(state),
    userStorage: homeSelectors.userStorage(state) || {},
    menusLoaded: homeSelectors.menusLoaded(state),
});

const mapDispatchToProps = {
    loadMenus: homeActions.loadMenus,
    toggleFavorite: homeActions.toggleFavorite,
};

export default connect(mapStateToProps, mapDispatchToProps)(AppWithStyleAndTranslation);
